import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, Phone, Mail, Calendar, MapPin, Users } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/api';

const STATUSES = ['pending', 'contacted', 'quoted', 'booked', 'completed', 'declined'];

const QuoteRequests = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const data = await adminApi.getQuoteRequests();
      setQuotes(data);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.updateQuoteStatus(id, status);
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
      toast({ title: 'Status updated' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.deleteQuoteRequest(id);
      setQuotes((prev) => prev.filter((q) => q.id !== id));
      toast({ title: 'Deleted' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const filtered = filter === 'all' ? quotes : quotes.filter((q) => q.status === filter);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#EADBC8] mb-2">Quote Requests</h1>
          <p className="text-gray-400">{quotes.length} total requests</p>
        </div>
        <div className="w-full sm:w-48">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#2A2420] border-[#C5A880]/20">
          <CardContent className="p-12 text-center text-gray-400">
            No quote requests found
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((q) => (
            <Card key={q.id} className="bg-[#2A2420] border-[#C5A880]/20" data-testid={`quote-${q.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold text-[#EADBC8]">{q.name}</h3>
                      <span className="text-xs px-3 py-1 rounded-full bg-[#C5A880]/10 text-[#C5A880] capitalize">
                        {q.eventType}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 mb-4 text-sm">
                      <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-gray-300 hover:text-[#C5A880]">
                        <Mail size={16} className="text-[#C5A880]" />
                        {q.email}
                      </a>
                      <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-gray-300 hover:text-[#C5A880]">
                        <Phone size={16} className="text-[#C5A880]" />
                        {q.phone}
                      </a>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Calendar size={16} className="text-[#C5A880]" />
                        {q.eventDate}
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <MapPin size={16} className="text-[#C5A880]" />
                        {q.venue}
                      </div>
                      {q.guestCount && (
                        <div className="flex items-center gap-2 text-gray-300">
                          <Users size={16} className="text-[#C5A880]" />
                          {q.guestCount} guests
                        </div>
                      )}
                    </div>

                    {/* Enquiry details */}
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 mb-4 text-xs text-gray-400">
                      {q.eventName && <div><span className="text-[#C5A880]">Event:</span> {q.eventName}</div>}
                      {(q.startTime || q.finishTime) && <div><span className="text-[#C5A880]">Booth:</span> {q.startTime || '?'}–{q.finishTime || '?'}</div>}
                      {q.venueAddress && <div className="sm:col-span-2"><span className="text-[#C5A880]">Address:</span> {q.venueAddress}</div>}
                      {q.packageChoice && <div className="sm:col-span-2"><span className="text-[#C5A880]">Package:</span> {q.packageChoice}</div>}
                      {(q.printingUpgrade || q.guestbook || q.redCarpet) && (
                        <div className="sm:col-span-2"><span className="text-[#C5A880]">Add-ons:</span>{' '}
                          {[q.printingUpgrade && 'Printing', q.guestbook && 'Guestbook', q.redCarpet && 'Red carpet'].filter(Boolean).join(', ')}
                        </div>
                      )}
                      {q.backdrop && <div><span className="text-[#C5A880]">Backdrop:</span> {q.backdrop}</div>}
                      {q.additionalHours && <div><span className="text-[#C5A880]">Extra hours:</span> {q.additionalHours}</div>}
                      {q.premiumBackdrop && <div className="sm:col-span-2"><span className="text-[#C5A880]">Premium backdrop:</span> {q.premiumBackdrop}</div>}
                      {q.hearAbout && <div className="sm:col-span-2"><span className="text-[#C5A880]">Heard via:</span> {q.hearAbout}</div>}
                      {q.services && <div className="sm:col-span-2"><span className="text-[#C5A880]">Service:</span> <span className="capitalize">{q.services}</span></div>}
                    </div>

                    {(q.otherRequirements || q.message) && (
                      <div className="mb-4 p-4 bg-[#1F1A17]/40 rounded-lg border border-[#C5A880]/10">
                        <p className="text-xs text-[#C5A880] font-semibold mb-1">OTHER REQUIREMENTS</p>
                        <p className="text-gray-300 text-sm whitespace-pre-line">{q.otherRequirements || q.message}</p>
                      </div>
                    )}

                    <p className="text-gray-500 text-xs">
                      Submitted: {new Date(q.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 lg:w-48">
                    <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                      <SelectTrigger className="bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8]" data-testid={`status-select-${q.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`delete-quote-${q.id}`}>
                          <Trash2 size={16} className="mr-2" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#2A2420] border-[#C5A880]/20">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-[#EADBC8]">Delete quote request?</AlertDialogTitle>
                          <AlertDialogDescription className="text-gray-400">
                            This will permanently delete the quote from {q.name}.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-[#3A332E] text-[#EADBC8] border-[#C5A880]/20">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(q.id)} className="bg-red-500 hover:bg-red-600 text-[#EADBC8]">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuoteRequests;
